import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DotsGrid, LayersTwo01 } from "@untitledui/icons";
import { useMemo } from "react";

import { cx } from "@/utils/cx";

import type { ShadowFloor } from "../../../types/location-map";

import { toTitleCase } from "../../../utils/shared/strings";

interface FloorReorderListProps {
	floors: ShadowFloor[];
	onReorder: (orderedFloorIds: string[]) => Promise<void> | void;
}

const SortableFloorReorderRow = ({ floor }: { floor: ShadowFloor }) => {
	const {
		attributes,
		isDragging,
		listeners,
		setNodeRef,
		transform,
		transition,
	} = useSortable({ id: floor.id });

	return (
		<div
			className={cx(
				"flex items-center gap-2 bg-primary px-2 py-3",
				isDragging && "bg-secondary_subtle relative z-10 shadow-md",
			)}
			ref={setNodeRef}
			style={{
				transform: CSS.Transform.toString(transform),
				transition,
			}}
		>
			<button
				aria-label={`Drag to reorder ${toTitleCase(floor.name)}`}
				className="flex size-8 shrink-0 cursor-grab items-center justify-center rounded-md text-fg-quaternary outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2 active:cursor-grabbing"
				type="button"
				{...attributes}
				{...listeners}
			>
				<DotsGrid className="size-5" />
			</button>
			<LayersTwo01 aria-hidden className="size-4 shrink-0 text-fg-quaternary" />
			<span className="min-w-0 flex-1">
				<span className="block text-[11px] leading-none font-medium tracking-wide text-tertiary uppercase">
					Floor
				</span>
				<span className="mt-0.5 block truncate text-sm font-semibold text-primary">
					{toTitleCase(floor.name)}
				</span>
			</span>
		</div>
	);
};

/**
 * Drag-and-drop list for floor zoom order.
 * The top of the list is the first zoom level.
 */
export const FloorReorderList = ({
	floors,
	onReorder,
}: FloorReorderListProps) => {
	const floorIds = useMemo(() => floors.map((floor) => floor.id), [floors]);

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: { distance: 6 },
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;

		const oldIndex = floorIds.indexOf(String(active.id));
		const newIndex = floorIds.indexOf(String(over.id));
		if (oldIndex < 0 || newIndex < 0) return;

		void onReorder(arrayMove(floorIds, oldIndex, newIndex));
	};

	return (
		<DndContext
			collisionDetection={closestCenter}
			onDragEnd={handleDragEnd}
			sensors={sensors}
		>
			<SortableContext items={floorIds} strategy={verticalListSortingStrategy}>
				<div className="divide-y divide-secondary overflow-hidden rounded-lg border border-secondary">
					{floors.map((floor) => (
						<SortableFloorReorderRow floor={floor} key={floor.id} />
					))}
				</div>
			</SortableContext>
		</DndContext>
	);
};
